import { describePlan, formatPrice, promotionPlan, type PromotionDoc } from '@talkofthetown/shared';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { callables, db } from '../firebase';
import { friendlyError } from '../lib/errors';
import { timeAgo } from '../lib/format';
import { useToast } from './Toasts';

interface Props {
  now: number;
  onOpenPost: (id: string) => void;
}

type Waiting = { orderId: string; promo: PromotionDoc; title: string };

/** Admin queue: paid promotions to show, or turn down (refunded in full). */
export function PromotionQueue({ now, onOpenPost }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<Waiting[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const snap = await getDocs(
        query(
          collection(db, 'promotions'),
          where('status', '==', 'review'),
          orderBy('createdAt', 'desc'),
          limit(50),
        ),
      );
      const waiting = await Promise.all(
        snap.docs.map(async (d) => {
          const promo = d.data() as PromotionDoc;
          const post = await getDoc(doc(db, 'posts', promo.postId));
          return { orderId: d.id, promo, title: (post.get('title') as string) ?? '(deleted post)' };
        }),
      );
      if (!cancelled) setItems(waiting);
    })().catch((err) => {
      if (cancelled) return;
      toast(friendlyError(err));
      setItems([]);
    });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  async function review(orderId: string, approve: boolean) {
    if (!approve && !window.confirm('Turn this promotion down and refund it in full?')) return;
    setBusy(orderId);
    try {
      await callables.reviewPromotion({ orderId, approve });
      toast(approve ? 'Promoted: it’s on the boards now' : 'Turned down and refunded');
      setItems((list) => (list ?? []).filter((x) => x.orderId !== orderId));
    } catch (err) {
      toast(friendlyError(err));
    } finally {
      setBusy(null);
    }
  }

  if (items === null || items.length === 0) return null;
  return (
    <section aria-labelledby="promotions-title">
      <h3 id="promotions-title">Paid promotions waiting</h3>
      <ul className="review__list">
        {items.map(({ orderId, promo, title }) => {
          const plan = promotionPlan(promo.plan);
          return (
            <li key={orderId} className="review__item">
              <button
                type="button"
                className="linklike review__title"
                onClick={() => onOpenPost(promo.postId)}
              >
                {title}
              </button>
              <span className="muted review__meta">
                {plan ? describePlan(plan) : promo.plan} ·{' '}
                {formatPrice(promo.amount, promo.currency)} paid{' '}
                {timeAgo(promo.paidAt ?? promo.createdAt, now)}
              </span>
              <span className="review__actions">
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={busy === orderId}
                  onClick={() => review(orderId, true)}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--danger"
                  disabled={busy === orderId}
                  onClick={() => review(orderId, false)}
                >
                  Refund
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
